import React from "react";

import { HeaderInfoWidget, TableColumn, TableRowAction, TableTitleAction } from "./models";
import { useTableColumns } from "../../../../hooks";
import { TableContent } from "./table-content";
import { noop } from "../../../../constants";
import { TableHeader } from "./table-header";
import { TableFooter } from "./table-footer";
import { LinkWidget } from "../link-widget";
import { TableTitle } from "./table-title";
import { Icons } from "../../../../enums";

import "./style.scss";

export interface TableProps {
  page?: number;
  rowKey?: string;
  rowClicked?: boolean,
  hideFooter?: boolean,
  hideHeader?: boolean,
  rowsCount?: number;
  loading?: boolean;
  className?: string;
  selectorValue?: number;
  rowClickedKey?: string;
  hideColumnHeaders?: boolean;
  onRefreshClick?: () => void;
  columns: Array<TableColumn>;
  leftActions?: React.ReactNode;
  rightActions?: React.ReactNode;
  title?: string | React.ReactNode;
  rows: Array<Record<string, any>>;
  handleColumnSorting?: () => void;
  tableColumnsRowActionIcon?: Icons;
  tableColumnsRowAction?: () => void;
  titleActions?: Array<TableTitleAction>;
  headerInfoWidgets?: Array<HeaderInfoWidget>;
  totals?: Record<string, React.ReactNode>;
  onRowClick?: (row: any, index: number) => void;
  onScroll?: React.UIEventHandler<HTMLDivElement>;
  onPaginationChange?: (page: number, selectorValue: number) => void;
  getRowActions?: (row: any, index: number) => Array<TableRowAction>;
  renderRowNestedChildren?: (row: Record<string, any>, index: number) => React.ReactNode;
}

export const Table: React.FC<TableProps> = ({
  rows,
  totals,
  columns,
  onScroll,
  page = 0,
  title = "",
  leftActions,
  hideFooter,
  hideHeader,
  titleActions,
  rightActions,
  getRowActions,
  rowKey = "id",
  onRefreshClick,
  className = "",
  loading = false,
  onRowClick = noop,
  selectorValue = 0,
  headerInfoWidgets,
  rowClickedKey = "",
  rowClicked = false,
  onPaginationChange,
  handleColumnSorting,
  tableColumnsRowAction,
  renderRowNestedChildren,
  rowsCount = rows.length,
  hideColumnHeaders = false,
  tableColumnsRowActionIcon = Icons.Edit,
}) => {

  const {cols} = useTableColumns(columns);
  const pagesCount =  Math.ceil(rowsCount / selectorValue);
  const showFooter = !loading && !!rowsCount && !hideFooter;

  return (
    <div className={`table ${className}`}>
      <div className="table-inner">
        {
            !hideHeader && (
                <TableTitle
                  title={title}
                  titleActions={titleActions}
                  onRefreshClick={onRefreshClick}
                />
            )
        }
        <div className="table-content">
          {
              headerInfoWidgets?.length && (
                  <div className="table-widget-wrapper">
                    {
                      headerInfoWidgets.map(({icon, iconColor = "007fdb", title, value, component}) => (
                          component || (
                              <LinkWidget
                                  icon={icon}
                                  title={title}
                                  value={value}
                                  iconColorHex={iconColor}
                                  className="jackpot-edit-info-item"
                              />
                          )
                      ))
                    }
                  </div>
              )
          }
          <TableHeader leftActions={leftActions} rightActions={rightActions}/>
          <TableContent
              rows={rows}
              columns={cols}
              rowKey={rowKey}
              totals={totals}
              loading={loading}
              noData={!rowsCount}
              onScroll={onScroll}
              rowClicked={rowClicked}
              onRowClick={onRowClick}
              rowClickedKey={rowClickedKey}
              getRowActions={getRowActions}
              hideColumnHeaders={hideColumnHeaders}
              handleColumnSorting={handleColumnSorting}
              tableColumnsRowAction={tableColumnsRowAction}
              renderRowNestedChildren={renderRowNestedChildren}
              tableColumnsRowActionIcon={tableColumnsRowActionIcon}
          />
        </div>
        {showFooter && (
            <TableFooter
                currentPage={page}
                rowsCount={rowsCount}
                pagesCount={pagesCount}
                pageRowsCount={rows.length}
                selectorValue={selectorValue}
                onPaginationChange={onPaginationChange}
            />
        )}
      </div>
    </div>
  );
};
